import { FC, Fragment, useRef, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useQuestionInterval from 'hooks/useQuestionInterval';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setQuestionReady, setQuestionStart, setAnswers, setQuestionEnd } from 'store/player/playerSlice';
import { selectQuestion, selectQuiz } from 'store/quiz/quizSelectors';
import { selectQuestionEnd } from 'store/player/playerSelectors';
import { IAnswerResult } from 'models/Answer';
// Components
import { Button } from 'components/Buttons';
import QuestionScoringBar from './QuestionScoringBar';
// Icons
import { IoLocationSharp } from 'react-icons/io5';

const ImagePin:FC = () => {
  const { t } = useTranslation();

  const imageRef = useRef<HTMLImageElement | null>(null);

  const dispatch = useAppDispatch();

  const quiz = useAppSelector(selectQuiz);
  const question = useAppSelector(selectQuestion);
  const questionEnd = useAppSelector(selectQuestionEnd);

  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [playerPos, setPlayerPos] = useState<{ x: number; y: number } | null>(null);
  const [score, setScore] = useState<number>(0);

  const { width, time } = useQuestionInterval(question.score);

  const tolerance = question.config?.tolerance || 0;
  const { x, y } = question.config || { x: 0, y: 0 };

  const onSubmit = () => {
    if (!playerPos) return;
    const correctPx = {
      x: x * imageSize.width,
      y: y * imageSize.height,
    };

    const playerPx = {
      x: playerPos.x * imageSize.width,
      y: playerPos.y * imageSize.height,
    };
    const dx = playerPx.x - correctPx.x;
    const dy = playerPx.y - correctPx.y;

    const distancePx = Math.sqrt(dx * dx + dy * dy);

    const tolerancePx = tolerance * base;

    let score = 0;

    const error = distancePx / tolerancePx;

    if (distancePx <= tolerancePx) {
      const percent = 1 - error;
      score = Math.round(question.score * percent);
    }

    setScore(score);

    const nextAnswer:IAnswerResult = {
      type: question.type,
      score,
      correct: score > 0,
      time,
      question: {
        ref: question.ref,
        title: question.title
      },
      input: {
        x: playerPos.x,
        y: playerPos.y,
        error
      }
    };

    dispatch(setAnswers([nextAnswer]));
    dispatch(setQuestionEnd(true));
    dispatch(setQuestionStart(false));
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    setPlayerPos({ x, y });
  };

  const handleImageLoad = () => {
    if (!imageRef.current) return;

    const { width, height } = imageRef.current.getBoundingClientRect();
    setImageSize({ width, height });

    dispatch(setQuestionReady(true));
    dispatch(setQuestionStart(true));
  };

  const base = Math.min(imageSize.width, imageSize.height);
  const correctColor = quiz.questionsSettings.correctAnswerColor;

  return (
    <div className="flex-1">
      <div className="relative w-full" onClick={handleClick}>
        <img
          ref={imageRef}
          src={question.config?.image}
          alt={question.title}
          className="w-full"
          onLoad={handleImageLoad}
        />

        {/* Correct pin */}
        {questionEnd && (
          <div
            className="absolute z-30"
            style={{
              top: `${y * 100}%`,
              left: `${x * 100}%`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <IoLocationSharp className="text-4xl pointer-events-none" style={{ color: correctColor }} />
          </div>
        )}

        {/* Player pin */}
        {playerPos && (
          <div
            className="absolute z-20"
            style={{
              top: `${playerPos.y * 100}%`,
              left: `${playerPos.x * 100}%`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <IoLocationSharp className="text-4xl text-blue-500 pointer-events-none" />
          </div>
        )}

        {/* Tolerance circle */}
        {tolerance && questionEnd && (
          <div
            className="absolute z-10 rounded-full border-2 border-white pointer-events-none"
            style={{
              width: `${tolerance * 2 * base}px`,
              height: `${tolerance * 2 * base}px`,
              left: `${x * 100}%`,
              top: `${y * 100}%`,
              transform: 'translate(-50%, -60%)',
            }}
          />
        )}
      </div>
      <div className="p-4">
        <QuestionScoringBar width={width} score={score} />
        <p className="mb-4 text-xl text-primary font-bold text-center">
        {question.title}
        </p>
        {questionEnd ? (
          <Fragment>
            {question.correctAnswerDescription ? (
              <p className="text-xl font-bold text-primary text-center">{question.correctAnswerDescription}</p>
            ) : null}
            {score ? (
              <p className="text-lg font-bold text-primary text-center">{`You scored ${score} points!`}</p>
            ) : null}
          </Fragment>
        ) : (
          <Fragment>
            <p className="mb-4 text-md text-primary font-bold text-center">Tap the map to drop a pin in that location</p>
            <Button
              className="w-full capitalize primary-button"
              onClick={onSubmit}
              disabled={questionEnd || !playerPos}
            >{t('buttons.submit')}</Button>
          </Fragment>
        )}
      </div>
    </div>
  );
}

export default ImagePin;
